import { type ReactNode, useId } from "react"

import type {
  GuildWarCandidatePoolMember,
  GuildWarDualRosterBoard,
  GuildWarFlatRosterBoard,
  GuildWarRosterBoard,
  GuildWarRosterTeamKey,
  GuildWarTeamRoster,
} from "@/core/guild-war/types"

import { BorderBeam } from "@/components/custom/border-beam"
import {
  SECTION_COLORS,
  TEAM_COLORS,
  type TeamColorKey,
} from "@/components/guild-war/team-colors"
import {
  getGuildWarRosterTeamKeys,
  getGuildWarTeamCapacity,
  getGuildWarTeamRosterMembers,
  hasGuildWarViceCommanderSlot,
  isDualGuildWarRosterBoard,
  isGuildWarFlatTeamRoster,
  isSpecializedGuildWarRosterBoard,
} from "@/core/guild-war/roster-board"
import { GUILD_WAR_TEAM_DISPLAY } from "@/core/guild-war/types"
import { toGuildWarPathPairDisplayLabel } from "@/lib/guild-war-path-display"
import { memberDisplayLabel } from "@/lib/member-display-label"

const TEAM_META: Array<{
  key: GuildWarRosterTeamKey
  emoji: string
  label: string
  accentClassName: string
  beamColorFrom: string
  beamColorTo: string
  flatMemberLaneCount: 1 | 2 | 3
}> = (["mobility", "attack", "defense"] as GuildWarRosterTeamKey[]).map(
  (key) => ({
    key,
    emoji: GUILD_WAR_TEAM_DISPLAY[key].emoji,
    label: GUILD_WAR_TEAM_DISPLAY[key].label,
    accentClassName: TEAM_COLORS[key].cardAccent,
    beamColorFrom: TEAM_COLORS[key].beamFrom,
    beamColorTo: TEAM_COLORS[key].beamTo,
    flatMemberLaneCount: key === "attack" ? 3 : key === "defense" ? 2 : 1,
  }),
)

export function PublishedRosterDashboard({
  board,
  candidateById,
  viewerTeamKey,
}: {
  board: GuildWarRosterBoard
  candidateById: ReadonlyMap<string, GuildWarCandidatePoolMember>
  viewerTeamKey: "mobility" | "attack" | "defense" | null
}) {
  const headingPrefix = useId()
  const isDual = isDualGuildWarRosterBoard(board)
  const usesCurrentFlatLayout =
    isSpecializedGuildWarRosterBoard(board) || isDual

  return usesCurrentFlatLayout ? (
    <FlatPublishedRosterBoard
      board={board}
      teamMeta={TEAM_META.filter(({ key }) =>
        getGuildWarRosterTeamKeys(board).includes(key),
      ).map((team) =>
        isDual ? { ...team, flatMemberLaneCount: 3 as const } : team,
      )}
      candidateById={candidateById}
      viewerTeamKey={viewerTeamKey}
      headingPrefix={headingPrefix}
    />
  ) : (
    <SquadOrCompatibilityPublishedRosterBoard
      board={board}
      candidateById={candidateById}
      viewerTeamKey={viewerTeamKey}
      headingPrefix={headingPrefix}
    />
  )
}

type PublishedBoardProps = {
  board: GuildWarRosterBoard
  candidateById: ReadonlyMap<string, GuildWarCandidatePoolMember>
  viewerTeamKey: "mobility" | "attack" | "defense" | null
  headingPrefix: string
}

type FlatPublishedBoardProps = Omit<PublishedBoardProps, "board"> & {
  board: GuildWarFlatRosterBoard | GuildWarDualRosterBoard
  teamMeta: typeof TEAM_META
}

function FlatPublishedRosterBoard({
  board,
  teamMeta,
  candidateById,
  viewerTeamKey,
  headingPrefix,
}: FlatPublishedBoardProps) {
  return (
    <div className="@container grid gap-4">
      {"mobility" in board && board.mobility ? (
        <FlatMobilityStrip
          board={board}
          team={board.mobility}
          candidateById={candidateById}
          viewerTeamKey={viewerTeamKey}
          headingId={`${headingPrefix}-mobility`}
        />
      ) : null}
      <div
        className={`grid min-w-0 items-start gap-4 ${
          teamMeta.length === 2
            ? "@min-[44rem]:grid-cols-2"
            : "@min-[64rem]:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"
        }`}
      >
        {teamMeta
          .filter(({ key }) => key !== "mobility")
          .map((teamMeta) => {
            const team = board[teamMeta.key]
            if (
              !team ||
              !isGuildWarFlatTeamRoster(team) ||
              !hasGuildWarViceCommanderSlot(team)
            ) {
              return null
            }

            return (
              <PublishedTeamCard
                key={teamMeta.key}
                teamMeta={teamMeta}
                team={team}
                board={board}
                candidateById={candidateById}
                isViewerTeam={viewerTeamKey === teamMeta.key}
                headingId={`${headingPrefix}-${teamMeta.key}`}
                containerResponsive
              />
            )
          })}
      </div>
      <SpecialAssignmentStrip
        title="Halftime"
        headingId={`${headingPrefix}-halftime`}
        memberIds={board.halftimePerformers}
        colorKey="halftime"
        board={board}
        candidateById={candidateById}
      />
      <SpecialAssignmentStrip
        title="Commanders"
        headingId={`${headingPrefix}-commanders`}
        memberIds={board.commanders}
        colorKey="commanders"
        board={board}
        candidateById={candidateById}
      />
    </div>
  )
}

function SquadOrCompatibilityPublishedRosterBoard(props: PublishedBoardProps) {
  return (
    <div className="grid gap-4">
      <div className="grid min-w-0 items-start gap-4 lg:grid-cols-3">
        {TEAM_META.filter(({ key }) =>
          getGuildWarRosterTeamKeys(props.board).includes(key),
        ).map((teamMeta) => {
          const team = props.board[teamMeta.key]
          if (!team) return null
          return (
            <PublishedTeamCard
              key={teamMeta.key}
              teamMeta={teamMeta}
              team={team}
              board={props.board}
              candidateById={props.candidateById}
              isViewerTeam={props.viewerTeamKey === teamMeta.key}
              headingId={`${props.headingPrefix}-${teamMeta.key}`}
            />
          )
        })}
      </div>
      <div className="grid gap-3 rounded-2xl border border-success/30 bg-success/10 p-4">
        <h3 className="text-lg font-semibold">Special Assignments</h3>
        <div className="grid gap-2 lg:grid-cols-2">
          <SpecialAssignmentStrip
            title="Halftime"
            headingId={`${props.headingPrefix}-halftime`}
            memberIds={props.board.halftimePerformers}
            board={props.board}
            candidateById={props.candidateById}
          />
          <SpecialAssignmentStrip
            title="Commanders"
            headingId={`${props.headingPrefix}-commanders`}
            memberIds={props.board.commanders}
            board={props.board}
            candidateById={props.candidateById}
          />
        </div>
      </div>
    </div>
  )
}

function PublishedTeamSectionShell({
  teamMeta,
  team,
  isViewerTeam,
  headingId,
  children,
}: {
  teamMeta: (typeof TEAM_META)[number]
  team: GuildWarTeamRoster
  isViewerTeam: boolean
  headingId: string
  children: ReactNode
}) {
  return (
    <section
      aria-labelledby={headingId}
      className={`relative grid min-w-0 gap-3 rounded-2xl border p-4 ${teamMeta.accentClassName}`}
    >
      {isViewerTeam ? (
        <BorderBeam
          size={150}
          duration={8}
          delay={0}
          colorFrom={teamMeta.beamColorFrom}
          colorTo={teamMeta.beamColorTo}
          borderWidth={2}
        />
      ) : null}
      <h3 id={headingId} className="text-lg font-semibold">
        {teamMeta.emoji} {teamMeta.label} (
        {`${getGuildWarTeamRosterMembers(team).length}/${getGuildWarTeamCapacity(team)}`}
        )
      </h3>
      {children}
    </section>
  )
}

function PublishedTeamCard({
  teamMeta,
  team,
  board,
  candidateById,
  isViewerTeam,
  headingId,
  containerResponsive = false,
}: {
  teamMeta: (typeof TEAM_META)[number]
  team: GuildWarTeamRoster
  board: GuildWarRosterBoard
  candidateById: ReadonlyMap<string, GuildWarCandidatePoolMember>
  isViewerTeam: boolean
  headingId: string
  containerResponsive?: boolean
}) {
  return (
    <PublishedTeamSectionShell
      teamMeta={teamMeta}
      team={team}
      isViewerTeam={isViewerTeam}
      headingId={headingId}
    >
      <div className="grid gap-2 rounded-xl border border-border/60 bg-background/70 p-3">
        <div
          className={`grid gap-2 ${hasGuildWarViceCommanderSlot(team) ? "sm:grid-cols-2" : ""}`}
        >
          <RoleSlot
            label="Leader"
            memberId={team.commander}
            board={board}
            candidateById={candidateById}
          />
          {hasGuildWarViceCommanderSlot(team) ? (
            <RoleSlot
              label="Vice"
              memberId={team.viceCommander}
              board={board}
              candidateById={candidateById}
            />
          ) : null}
        </div>
      </div>
      <div className="grid gap-2 rounded-xl border border-border/60 bg-background/70 p-3">
        {isGuildWarFlatTeamRoster(team) ? (
          <MemberBlock
            memberIds={team.members}
            laneCount={teamMeta.flatMemberLaneCount}
            containerResponsive={containerResponsive}
            board={board}
            candidateById={candidateById}
          />
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            <MemberBlock
              title="Squad A"
              memberIds={team.subteams[0].members}
              board={board}
              candidateById={candidateById}
            />
            <MemberBlock
              title="Squad B"
              memberIds={team.subteams[1].members}
              board={board}
              candidateById={candidateById}
            />
          </div>
        )}
      </div>
    </PublishedTeamSectionShell>
  )
}

function FlatMobilityStrip({
  board,
  team,
  candidateById,
  viewerTeamKey,
  headingId,
}: Omit<PublishedBoardProps, "headingPrefix"> & {
  team: GuildWarTeamRoster
  headingId: string
}) {
  const teamMeta = TEAM_META[0]!
  if (!isGuildWarFlatTeamRoster(team) || !hasGuildWarViceCommanderSlot(team)) {
    return null
  }

  return (
    <PublishedTeamSectionShell
      teamMeta={teamMeta}
      team={team}
      isViewerTeam={viewerTeamKey === "mobility"}
      headingId={headingId}
    >
      <div className="grid min-w-0 gap-2 rounded-xl border border-border/60 bg-background/70 p-3 @min-[64rem]:grid-cols-[repeat(5,minmax(0,1fr))]">
        <RoleSlot
          label="Leader"
          memberId={team.commander}
          board={board}
          candidateById={candidateById}
        />
        <RoleSlot
          label="Vice"
          memberId={team.viceCommander}
          board={board}
          candidateById={candidateById}
        />
        <MemberLane
          entries={team.members.map((memberId, index) => ({ memberId, index }))}
          className="@min-[64rem]:col-span-3 @min-[64rem]:grid @min-[64rem]:grid-cols-3 @min-[64rem]:gap-2 @min-[64rem]:space-y-0"
          board={board}
          candidateById={candidateById}
        />
      </div>
    </PublishedTeamSectionShell>
  )
}

function RoleSlot({
  label,
  memberId,
  board,
  candidateById,
}: {
  label: string
  memberId: string | null
  board: GuildWarRosterBoard
  candidateById: ReadonlyMap<string, GuildWarCandidatePoolMember>
}) {
  const member = resolveMember(memberId, board, candidateById)

  return (
    <div className="min-w-0 rounded-lg border border-border/60 bg-background/80 p-2.5">
      <p className="text-kicker tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 truncate font-medium">{member.name}</p>
      <p className="truncate text-xs text-muted-foreground">
        {member.pathLabel}
      </p>
    </div>
  )
}

function partitionIntoBalancedLanes<T>(
  items: readonly T[],
  requestedLaneCount: number,
) {
  if (items.length === 0) return []

  const laneCount = Math.min(requestedLaneCount, items.length)
  const baseLength = Math.floor(items.length / laneCount)
  const remainder = items.length % laneCount
  const lanes: Array<Array<{ memberId: T; index: number }>> = []
  let offset = 0

  for (let laneIndex = 0; laneIndex < laneCount; laneIndex += 1) {
    const laneLength = baseLength + (laneIndex < remainder ? 1 : 0)
    lanes.push(
      Array.from({ length: laneLength }, (_, index) => ({
        memberId: items[offset + index]!,
        index: offset + index,
      })),
    )
    offset += laneLength
  }

  return lanes
}

function MemberLane({
  entries,
  board,
  candidateById,
  className = "",
}: {
  entries: Array<{ memberId: string | null; index: number }>
  board: GuildWarRosterBoard
  candidateById: ReadonlyMap<string, GuildWarCandidatePoolMember>
  className?: string
}) {
  return (
    <ul className={`min-w-0 space-y-1 text-sm ${className}`}>
      {entries.map(({ memberId, index }) => {
        const member = resolveMember(memberId, board, candidateById)
        return (
          <li
            key={index}
            className="min-w-0 rounded-md border border-border/50 bg-background/70 px-2 py-1.5"
          >
            <p className="truncate font-medium">{member.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {member.pathLabel}
            </p>
          </li>
        )
      })}
    </ul>
  )
}

function MemberBlock({
  title,
  memberIds,
  laneCount = 1,
  containerResponsive = false,
  board,
  candidateById,
}: {
  title?: string
  memberIds: Array<string | null>
  laneCount?: 1 | 2 | 3
  containerResponsive?: boolean
  board: GuildWarRosterBoard
  candidateById: ReadonlyMap<string, GuildWarCandidatePoolMember>
}) {
  const lanes = partitionIntoBalancedLanes(memberIds, laneCount)
  const columnClass =
    laneCount === 3
      ? containerResponsive
        ? "@min-[64rem]:grid-cols-3"
        : "lg:grid-cols-3"
      : laneCount === 2
        ? containerResponsive
          ? "@min-[64rem]:grid-cols-2"
          : "lg:grid-cols-2"
        : ""

  return (
    <div className="min-w-0 rounded-lg border border-border/60 bg-background/80 p-2.5">
      {title ? (
        <p className="text-kicker tracking-[0.14em] text-muted-foreground">
          {title}
        </p>
      ) : null}
      <div className={`mt-1 grid min-w-0 gap-2 ${columnClass}`}>
        {lanes.map((lane) => (
          <MemberLane
            key={lane[0]!.index}
            entries={lane}
            board={board}
            candidateById={candidateById}
          />
        ))}
      </div>
    </div>
  )
}

function SpecialAssignmentStrip({
  title,
  headingId,
  memberIds,
  colorKey,
  board,
  candidateById,
}: {
  title: string
  headingId: string
  memberIds: Array<string | null>
  colorKey?: "halftime" | "commanders"
  board: GuildWarRosterBoard
  candidateById: ReadonlyMap<string, GuildWarCandidatePoolMember>
}) {
  const filledCount = memberIds.filter(Boolean).length
  const colors = colorKey ? SECTION_COLORS[colorKey] : null

  const heading = (
    <h3
      id={headingId}
      className={`text-kicker text-muted-foreground ${colors?.title ?? ""}`}
    >
      {title} ({filledCount}/{memberIds.length})
    </h3>
  )

  const assignments = (
    <ol
      className={
        colors
          ? `grid min-w-0 gap-1.5 p-3 text-sm ${
              colorKey === "halftime"
                ? "@min-[64rem]:grid-cols-3"
                : "@min-[64rem]:grid-cols-5"
            }`
          : "mt-2 space-y-1.5 text-sm"
      }
    >
      {memberIds.map((memberId, index) => {
        const member = resolveMember(memberId, board, candidateById)
        return (
          <li
            key={`${title}-${index}`}
            className={`min-w-0 rounded-md border border-border/50 bg-background/80 px-2.5 py-2 ${colors?.slot ?? ""}`}
          >
            <p className="truncate font-medium">
              <span aria-hidden="true">{index + 1}. </span>
              {member.name}
            </p>
            <p
              className={`truncate text-xs ${colors ? "text-current opacity-70" : "text-muted-foreground"}`}
            >
              {member.pathLabel}
            </p>
          </li>
        )
      })}
    </ol>
  )

  return (
    <section
      aria-labelledby={headingId}
      className={`min-w-0 rounded-xl border border-border/60 bg-background/70 ${colors ? colors.card : "p-3"}`}
    >
      {colors ? (
        <>
          <div className={`rounded-t-xl p-3 ${colors.header}`}>{heading}</div>
          {assignments}
        </>
      ) : (
        <>
          {heading}
          {assignments}
        </>
      )}
    </section>
  )
}

function resolveMember(
  memberId: string | null,
  board: GuildWarRosterBoard,
  candidateById: ReadonlyMap<string, GuildWarCandidatePoolMember>,
) {
  if (!memberId) {
    return { name: "Vacant", pathLabel: "Not set" }
  }

  const candidate = candidateById.get(memberId)
  const snapshot = board.memberPaths[memberId]
  const pathLabel =
    snapshot?.primaryPathCode || snapshot?.secondaryPathCode
      ? toGuildWarPathPairDisplayLabel(
          snapshot.primaryPathCode,
          snapshot.secondaryPathCode,
          { variant: "short", noneLabel: "Not set" },
        )
      : toGuildWarPathPairDisplayLabel(
          candidate?.primaryPathCode,
          candidate?.secondaryPathCode,
          { variant: "short", noneLabel: "Not set" },
        )

  return {
    name: memberDisplayLabel({
      displayName:
        board.memberDisplayNames[memberId] ?? candidate?.guildDisplayName,
    }),
    pathLabel,
  }
}
